import produce, {Draft} from 'immer';
import React, {useReducer} from 'react';

import {assert} from 'Asserts';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  INITIAL_WORKSHEET_VIEW_STATE,
  WorksheetViewActionType,
  WorksheetViewState,
  WorksheetViewStateAccessible,
  WorksheetViewStateAction,
  WorksheetViewStateInaccessible,
  WorksheetViewStateType,
} from 'notebook/worksheets/WorksheetConstants';
import {isExecutionEnabled} from 'notebook/worksheets/workspace/WorksheetCapabilityManagementHelpers';
import {assertNever} from 'util/Types';

export function useWorksheetReducer(): [Draft<WorksheetViewState>, React.Dispatch<WorksheetViewStateAction>] {
  return useReducer(worksheetViewStateReducers, INITIAL_WORKSHEET_VIEW_STATE);
}

export const worksheetViewStateReducers = produce(
  (draft: Draft<WorksheetViewState>, action: WorksheetViewStateAction) => {
    switch (action.type) {
      case WorksheetViewActionType.RELOADING:
        if (isInaccessible(action.worksheet)) {
          draft.state = WorksheetViewStateType.INACCESSIBLE;
          (draft as WorksheetViewStateInaccessible).worksheetIsInaccessible = true;
          (draft as WorksheetViewStateInaccessible).worksheet = action.worksheet;
        } else {
          draft.state = WorksheetViewStateType.RELOADING;
          (draft as WorksheetViewStateAccessible).worksheet = action.worksheet;
          (draft as WorksheetViewStateAccessible).isExecutionEnabled = false;
        }
        break;
      case WorksheetViewActionType.LOADED:
        if (isInaccessible(action.worksheet)) {
          draft.state = WorksheetViewStateType.INACCESSIBLE;
          (draft as WorksheetViewStateInaccessible).worksheetIsInaccessible = true;
          (draft as WorksheetViewStateInaccessible).worksheet = action.worksheet;
        } else {
          draft.state = WorksheetViewStateType.LOADED;
          (draft as WorksheetViewStateAccessible).worksheet = action.worksheet;
          // Use the action value of `isExecutionEnabled` if it is provided
          // If not, use the last state of `isExecutionEnabled if it was enabled before
          // This is needed since we don't always refetch the ProcedureRun when we switch worksheets
          (draft as WorksheetViewStateAccessible).isExecutionEnabled =
            action.isExecutionEnabled ?? (draft as WorksheetViewStateAccessible).isExecutionEnabled ?? false;
        }
        break;
      case WorksheetViewActionType.PROCEDURE_LOADED:
        assert(
          draft.state === WorksheetViewStateType.LOADED,
          'The Procedure was loaded before the Worksheet'
        );
        if (action.procedureType == null) {
          draft.isExecutionEnabled = true;
        } else {
          draft.isExecutionEnabled = isExecutionEnabled(action.procedureType);
        }
        break;
      default:
        assert(false, `Unexpected action type ${assertNever(action)}`);
    }
  }
);
